import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../auth/hooks/useAuth';

/**
 * Props for the ProtectedRoute component
 * @interface ProtectedRouteProps
 * @property {React.ReactNode} children - The child components to render when authenticated
 * @property {string} [redirectTo] - The path to redirect to when not authenticated
 */
interface ProtectedRouteProps {
  children: React.ReactNode;
  redirectTo?: string;
}

/**
 * A wrapper component that protects routes requiring authentication
 * @component
 * @param {ProtectedRouteProps} props - Component props
 * @param {React.ReactNode} props.children - The child components to render when authenticated
 * @param {string} [props.redirectTo='/login'] - The path to redirect to when not authenticated
 * @returns {React.ReactElement} Protected route component
 */
const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  redirectTo = '/login' 
}) => {
  const { session, loading } = useAuth();
  const location = useLocation();

  console.log('ProtectedRoute - State:', {
    loading,
    hasSession: !!session,
    userId: session?.user?.id,
    userEmail: session?.user?.email,
    currentPath: location.pathname,
    redirectTo
  });

  // Prevent infinite redirects
  if (redirectTo === location.pathname) {
    console.error('ProtectedRoute: Redirect path matches current location, preventing infinite loop');
    return null;
  }

  // If we're loading, show a loading indicator
  if (loading) {
    console.log('ProtectedRoute - Still loading auth...');
    return (
      <div className="min-h-screen flex items-center justify-center bg-black">
        <div className="cyber-text animate-blink">CARREGANDO...</div>
      </div>
    );
  }

  // If there's no session, redirect to login
  if (!session?.user?.id) {
    console.log('ProtectedRoute - No session found, redirecting to:', redirectTo);
    // Store the attempted path for post-login redirect
    const currentPath = location.pathname + location.search + location.hash;
    if (currentPath !== '/login') {
      sessionStorage.setItem('redirectAfterLogin', currentPath);
    }
    return <Navigate to={redirectTo} replace />;
  }

  console.log('ProtectedRoute - User authenticated, rendering children');
  return <>{children}</>;
};

export default ProtectedRoute;
